import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import {
  ApiHttpError,
  getFriendlyMessage,
  isProblemDetailsBody,
  mapHttpStatusToEnum,
  resolveProblemMessage,
} from './api-http-error.model';
import { buildApiUrl } from './api-url.util';

type Params = HttpParams | Record<string, string | number>;

/** Monta a URL da API e converte falhas em `ApiHttpError`. Autenticação: `authInterceptor`. */
@Injectable({ providedIn: 'root' })
export class HttpClientService {
  private readonly httpClient = inject(HttpClient);

  get<T>(path: string, options?: { params?: Params }): Observable<T> {
    return this.request(this.httpClient.get<T>(buildApiUrl(path), { params: options?.params }));
  }

  /** Em erro o corpo também vem como Blob, então a mensagem sai do status. */
  getBlob(path: string): Observable<Blob> {
    return this.request(this.httpClient.get(buildApiUrl(path), { responseType: 'blob' }));
  }

  post<T>(path: string, body: unknown): Observable<T> {
    return this.request(this.httpClient.post<T>(buildApiUrl(path), body));
  }

  put<T>(path: string, body: unknown): Observable<T> {
    return this.request(this.httpClient.put<T>(buildApiUrl(path), body));
  }

  patch<T>(path: string, body: unknown): Observable<T> {
    return this.request(this.httpClient.patch<T>(buildApiUrl(path), body));
  }

  delete<T = void>(path: string): Observable<T> {
    return this.request(this.httpClient.delete<T>(buildApiUrl(path)));
  }

  private request<T>(source: Observable<T>): Observable<T> {
    return source.pipe(catchError((error: unknown) => this.handleError(error)));
  }

  private handleError(error: unknown): Observable<never> {
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(
        () =>
          new ApiHttpError(
            mapHttpStatusToEnum(0),
            error instanceof Error ? error.message : 'Unexpected HTTP error.',
          ),
      );
    }

    const problem = isProblemDetailsBody(error.error) ? error.error : undefined;
    const message = resolveProblemMessage(problem, getFriendlyMessage(error.status));

    return throwError(() => new ApiHttpError(mapHttpStatusToEnum(error.status), message, problem));
  }
}
